"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { Card, CardBody, CardHeader, Badge, Input, Row, Col } from "reactstrap"
import { FaEdit, FaTrash, FaPlus, FaSearch } from "react-icons/fa"
import DataTable, { type TableColumn } from "react-data-table-component"
import { Btn } from "../../../AbstractElements"
import { TurnstileService } from "../../../Service/TurnstileService"
import type { Turnstile, PaginatedResponse } from "./types"
import { toast } from "react-toastify"
import Swal from "sweetalert2"

interface Props {
  onAdd: () => void
  onEdit: (turnstile: Turnstile) => void
  refreshTrigger: boolean
}

const TurnstileList: React.FC<Props> = ({ onAdd, onEdit, refreshTrigger }) => {
  const [turnstiles, setTurnstiles] = useState<Turnstile[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState("")
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    pageSize: 10,
  })

  const fetchTurnstiles = async (page = 1, searchTerm = "") => {
    try {
      setLoading(true)
      const response: PaginatedResponse<Turnstile> = await TurnstileService.getAll(
        page,
        pagination.pageSize,
        searchTerm,
      )
      setTurnstiles(response.results)
      setPagination({
        currentPage: response.current_page,
        totalPages: response.total_pages,
        totalCount: response.count,
        pageSize: response.page_size,
      })
    } catch (error) {
      console.error("Failed to fetch turnstiles:", error)
      toast.error("Failed to load turnstiles")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchTurnstiles()
  }, [refreshTrigger])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    fetchTurnstiles(1, search)
  }

  const handleDelete = async (turnstile: Turnstile) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: `Delete turnstile ${turnstile.number}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    })

    if (result.isConfirmed) {
      try {
        await TurnstileService.delete(turnstile.id)
        await Swal.fire("Deleted!", "Turnstile has been deleted.", "success")
        fetchTurnstiles(pagination.currentPage, search)
      } catch (error) {
        console.error("Failed to delete turnstile:", error)
        await Swal.fire("Error", "Failed to delete turnstile. It may have active assignments.", "error")
      }
    }
  }

  const getStatusBadge = (status: string) => {
    const statusColors = {
      active: "success",
      inactive: "secondary",
      maintenance: "warning",
    }
    return (
      <Badge color={statusColors[status as keyof typeof statusColors] || "secondary"} pill>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    )
  }

  const handlePageChange = (page: number) => {
    fetchTurnstiles(page, search)
  }

  const handlePerRowsChange = (newPerPage: number, page: number) => {
    setPagination((prev) => ({ ...prev, pageSize: newPerPage }))
    fetchTurnstiles(page, search)
  }

  const columns: TableColumn<Turnstile>[] = [
    
    {
      name: "Number",
      selector: (row) => row.number,
      sortable: true,
      cell: (row) => <strong>{row.number}</strong>,
    },
    {
      name: "Status",
      selector: (row) => row.status,
      sortable: true,
      cell: (row) => getStatusBadge(row.status),
    },
    {
      name: "Actions",
      cell: (row) => (
        <div className="d-flex gap-2">
          <Btn color="info" size="xs" onClick={() => onEdit(row)} title="Edit">
            <FaEdit />
          </Btn>
          <Btn color="danger" size="xs" onClick={() => handleDelete(row)} title="Delete">
            <FaTrash />
          </Btn>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ]

  return (
    <Card>
      <CardHeader>
        <Row className="align-items-center">
          <Col md="6">
            <h5 className="mb-0">Turnstiles ({pagination.totalCount})</h5>
          </Col>
          <Col md="6" className="text-end">
            <Btn color="primary" size="sm" onClick={onAdd}>
              <FaPlus className="me-2" />
              Add Turnstile
            </Btn>
          </Col>
        </Row>
      </CardHeader>
      <CardBody>
        {/* Search */}
        <form onSubmit={handleSearch} className="mb-3">
          <Row>
            <Col md="4">
              <div className="input-group">
                <Input
                  type="text"
                  placeholder="Search turnstiles..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <button className="btn btn-outline-secondary btn-sm" type="submit">
                  <FaSearch />
                </button>
              </div>
            </Col>
          </Row>
        </form>

        {/* DataTable */}
        <DataTable
          columns={columns}
          data={turnstiles}
          progressPending={loading}
          pagination
          paginationServer
          paginationTotalRows={pagination.totalCount}
          paginationDefaultPage={pagination.currentPage}
          paginationPerPage={pagination.pageSize}
          paginationRowsPerPageOptions={[10, 20, 30, 50]}
          onChangeRowsPerPage={handlePerRowsChange}
          onChangePage={handlePageChange}
          noDataComponent="No turnstiles found"
          striped
          highlightOnHover
          responsive
        />
      </CardBody>
    </Card>
  )
}

export default TurnstileList
